import React, { useEffect, useMemo, useState } from 'react';
import Zana, { ZanaState } from './Zana';
import { fetchZanaBriefing } from '../services/briefingsApi';
import { intentsFromInsightText, ZanaIntentExecutor } from '../services/zanaActions';
import ZanaActionChips from './ZanaActionChips';
import { customToast } from '../utils/customToast';

/* ZanaBand — the purple one-line "ZANA" AI layer shown at the top of the
   Fuel / Routes / Drivers / Incidents tabs for premium orgs. Reads
   aiInsights/{orgCode}__{tab}. The AI layer is ALWAYS present for premium: until
   an insight exists Zana sits in her idle "thinking" state and says she's
   configuring / learning that tab — she never just disappears. Only mounted
   inside BusAnalyticsPage (premium context).

   Phase 3: insight text maps to Action Registry chips (same allow-list as Ask). */

const TAB_WORD: Record<string, string> = {
  fuel: 'fuel', routes: 'routes', drivers: 'driver', incidents: 'incident',
};

export default function ZanaBand({
  orgCode,
  tab,
  onExecuteIntent,
  liveUnreadAlertCount = 0,
}: {
  orgCode: string | null;
  tab: string;
  onExecuteIntent?: ZanaIntentExecutor;
  liveUnreadAlertCount?: number;
}) {
  const [text, setText] = useState<string>('');
  const [state, setState] = useState<ZanaState>('online');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!orgCode) { setLoaded(true); return; }
    let alive = true;
    setLoaded(false);
    setText('');
    // Read the per-tab line from the briefing via the backend (perTab[tab]) —
    // client-side aiInsights reads are blocked by rules for some org admins.
    fetchZanaBriefing(orgCode)
      .then((brief) => {
        if (!alive) return;
        const t = brief && brief.perTab ? String(brief.perTab[tab] || '') : '';
        setText(t);
        setState((brief && (brief.mascotState as ZanaState)) || 'online');
        setLoaded(true);
      })
      .catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [orgCode, tab]);

  const intents = useMemo(
    () => intentsFromInsightText(text, { liveUnreadCount: liveUnreadAlertCount, tab }),
    [text, liveUnreadAlertCount, tab],
  );

  // Idle / configuring — Zana is present and reading this tab in the background,
  // even with nothing specific to say yet (thinking face + "configuring" copy).
  if (!text) {
    return (
      <div className="zana-band">
        <Zana state="thinking" size={28} animated />
        <span className="zana-band-pill">ZANA</span>
        <span className="zana-band-text">
          {loaded
            ? `Zana is configuring — reading your ${TAB_WORD[tab] || tab} data. Insights land here as she learns your fleet.`
            : 'Connecting to Zana…'}
        </span>
      </div>
    );
  }

  return (
    <div className="zana-band" style={{ flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <Zana state={state} size={28} animated />
      <span className="zana-band-pill">ZANA</span>
      <span className="zana-band-text" style={{ flex: '1 1 220px' }}>{text}</span>
      {onExecuteIntent && intents.length > 0 && (
        <div style={{ flex: '1 1 100%', paddingLeft: 36 }}>
          <ZanaActionChips
            intents={intents}
            onExecuteIntent={onExecuteIntent}
            tone="light"
            onResult={(line) => customToast.show(line, 'success')}
          />
        </div>
      )}
    </div>
  );
}
